import React, { useEffect, useState } from "react";
import { Route, Switch, useLocation, useHistory } from "react-router-dom";
import { createTheme, ThemeProvider } from "@material-ui/core";
import MarketingContextProvider from "./contexts/MarketingContext";
import {
  selectLoadInitialData,
  selectCleverlynk,
  selectCompany,
  selectLogo,
  loadInitialData,
  selectCurrentClynkWithinGroup,
  selectCleverlynkGroup,
  selectCredentials,
  setGoogleClientId,
  selectTriggerNavToUserForm,
  selectZendesk,
  fetchCompanyBillingData,
} from "./redux/slices/cleverlynkSlice";
import { initCatalog, ITemplateInfoForRender } from "./redux/slices/templateSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "./hooks/useLocalStorage";
import ClynkSnackbar from "./components/Tools/ClynkSnackbar";
import useCredentials from "./hooks/useCredentials";
import useCustomFonts from "./hooks/useCustomFonts";
import useDeliveryAddressCookies from "./hooks/MapServiceHooks/useDeliveryAddressCookies";
import useFavicon from "./hooks/useFavicon";
import useMapService from "./hooks/MapServiceHooks/useMapService";
import useMarketingTags from "./hooks/useMarketingTags";
import useTemplateStyles from "./hooks/useTemplateStyles";
import useUserFormFunctions from "./hooks/useUserFormFunctions";
import useDisableOverflow from "./hooks/useDisableOverflow";
import useIdentificationCookies from "./hooks/useIdentificationCookies";

import { useTranslation } from "react-i18next";
import "./i18n/config";
import Landing from "./pages/Landing";
import { PFXLandingPageResponse, PFXSKU } from "./models/Interfaces";
import OpaqueLoading from "./components/Tools/OpaqueLoading";
import useLandingFunctions from "./hooks/useLandingFunctions";
import useCleverlynkFunctions from "./hooks/useCleverlynkFunctions";
import OpaqueItemLoading from "./components/Tools/OpaqueItemLoading";
import { ALIMENTEC_LIST } from "./components/UserForm/IdentificationField";
import { SIC_LIST } from "./components/Footer/Footer";

const Checkout = React.lazy(() => import("./pages/CleverlynkCheckout"));
const OrderSummary = React.lazy(() => import("./pages/OrderSummary"));
const Template = React.lazy(() => import("./pages/Template"));
const UnavailableDialog = React.lazy(() => import("./components/Tools/UnavailableDialog"));
const UserForm = React.lazy(() => import("./pages/UserForm"));
const Coverage = React.lazy(() => import("./pages/Coverage"));

const colorAdjust = (color: string, amount: number) => {
  return (
    "#" +
    color
      .replace(/^#/, "")
      .replace(/../g, color => ("0" + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2))
  );
};

const createThemeColor = (primaryColor: string, fonts: { title?: string; text?: string } | undefined) => {
  return createTheme({
    zIndex: {
      tooltip: 5000,
      modal: 4900,
      snackbar: 9990,
    },
    palette: {
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: colorAdjust(primaryColor, 60),
      },
      warning: {
        main: "#ffbf00",
      },
      error: {
        main: "#eb4034",
      },
      success: {
        main: "#76A30F",
      },
    },
    typography: {
      h1: {
        fontFamily: fonts?.title ?? "Inter",
      },
      h2: {
        fontFamily: fonts?.title ?? "Inter",
      },
      h3: {
        fontFamily: fonts?.title ?? "Inter",
      },
      h4: {
        fontFamily: fonts?.title ?? "Inter",
      },
      h5: {
        fontFamily: fonts?.title ?? "Inter",
      },
      h6: {
        fontFamily: fonts?.title ?? "Inter",
      },
      subtitle1: {
        fontFamily: fonts?.text ?? "Inter",
      },
      subtitle2: {
        fontFamily: fonts?.text ?? "Inter",
      },
      body1: {
        fontFamily: fonts?.text ?? "Inter",
      },
      body2: {
        fontFamily: fonts?.text ?? "Inter",
      },
      button: {
        fontFamily: fonts?.title ?? "Inter",
      },
      caption: {
        fontFamily: fonts?.text ?? "Inter",
      },
    },
  });
};

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const App: React.FC = () => {
  const [loadedMarketingScripts, setLoadedMarketingScripts] = useState<boolean>(false);
  // async selectors
  const loadInitialDataSupervisor = useSelector(selectLoadInitialData);
  // state selectors
  const cleverlynk = useSelector(selectCleverlynk);
  const cleverlynkGroup = useSelector(selectCleverlynkGroup);
  const company = useSelector(selectCompany);
  const logo = useSelector(selectLogo);
  const chosenClynkWithinGroup = useSelector(selectCurrentClynkWithinGroup);
  const credentials = useSelector(selectCredentials);
  const navigateToForm = useSelector(selectTriggerNavToUserForm);
  const zendesk = useSelector(selectZendesk);

  const marketingTags = useMarketingTags();
  const favicon = useFavicon();
  const { titleStyles, primaryColor } = useTemplateStyles();
  const loadMeta = React.useRef<boolean>(false);
  const switchBetweenClynksRef = React.useRef<boolean>(false);
  const loadedChatScript = React.useRef<boolean>(false);
  const loadedBillingData = React.useRef<boolean>(false);

  const { i18n } = useTranslation();

  const { resetCriteria } = useUserFormFunctions();
  const { init: initDeliveryAddressCookies } = useDeliveryAddressCookies();
  const { init: initIdentificationCookies } = useIdentificationCookies();
  const { handleGetActiveGroupMembers, handleSetNoLandings: setNoLanding } = useCleverlynkFunctions();
  const { handleGoToCatalog } = useLandingFunctions();
  const { handleLoadLandingPage } = useLandingFunctions();
  const { enable } = useDisableOverflow();
  const { handleInitAWSClient, handleLocateCityFromClynk } = useMapService();

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  useLocalStorage();
  useCredentials();
  useCustomFonts();

  const loadedInitialData = React.useMemo(
    () => loadInitialDataSupervisor.status === "succeeded",
    [loadInitialDataSupervisor]
  );

  useEffect(() => {
    if (!location.pathname.includes("order/summary")) {
      if (!loadedInitialData) {
        dispatch(
          loadInitialData({
            location,
            initTemplate: (params: ITemplateInfoForRender, skuList: PFXSKU[]) =>
              dispatch(initCatalog({ ...params, skuList })),
            initLanding: (lnding: PFXLandingPageResponse, groupLandingId: string | undefined) =>
              handleLoadLandingPage(lnding, groupLandingId, setNoLanding),
            historyPush: (param: string) => {
              window.location.href = param;
            },
            handleSetNoLanding: (b: boolean) => {
              setNoLanding(b);
              if (b) handleGoToCatalog(true);
            },
          })
        );
      }
    }
  }, []);

  useEffect(() => {
    handleLocateCityFromClynk();
  }, [loadInitialDataSupervisor]);

  useEffect(() => {
    if (cleverlynkGroup && cleverlynkGroup.id) {
      handleGetActiveGroupMembers(cleverlynkGroup?.id);
    }
  }, [cleverlynkGroup]);

  useEffect(() => {
    if (
      cleverlynkGroup &&
      chosenClynkWithinGroup &&
      (chosenClynkWithinGroup.id !== cleverlynk?.id || chosenClynkWithinGroup.id !== location.pathname.split("/")[1])
    ) {
      switchBetweenClynksRef.current = true;
      dispatch(
        loadInitialData({
          id: chosenClynkWithinGroup.id,
          location,
          initTemplate: (params: ITemplateInfoForRender, skuList: PFXSKU[]) =>
            dispatch(initCatalog({ ...params, skuList })),
          initLanding: (lnding: PFXLandingPageResponse, groupLandingId: string | undefined) =>
            handleLoadLandingPage(lnding, groupLandingId, setNoLanding),
          historyPush: (param: string) => {
            window.location.href = param;
          },
          handleSetNoLanding: (b: boolean) => {
            setNoLanding(b);
            if (b) handleGoToCatalog(true);
          },
        })
      );
    }
  }, [chosenClynkWithinGroup]);

  useEffect(() => {
    if (zendesk && !loadedChatScript.current && !ALIMENTEC_LIST.includes(cleverlynk?.id ?? "")) {
      loadedChatScript.current = true;
      const script = document.createElement("script");
      script.id = "ze-snippet";
      script.src = zendesk;
      document.body.appendChild(script);
    }
  }, [zendesk]);

  useEffect(() => {
    let fbPixel, ga, gtm;
    if (credentials && !loadedMarketingScripts) {
      if (credentials.FB?.pixelId) {
        fbPixel = marketingTags.initFacebookPixel(credentials.FB?.pixelId);
      }
      if (credentials.GA?.googleAnalyticsId) {
        ga = marketingTags.initGoogleAnalytics(credentials.GA?.crossDomainGA, credentials.GA?.googleAnalyticsId);
      }
      if (credentials.GTM?.googleTagManagerId) {
        gtm = marketingTags.initGoogleTagManager(credentials.GTM?.googleTagManagerId);
      }
      if (ga) {
        //@ts-ignore
        window.gclidPromise.then(gclid => dispatch(setGoogleClientId(gclid)));
      }

      setLoadedMarketingScripts(fbPixel || ga || gtm);
    }
  }, [credentials]);

  useEffect(() => {
    if (cleverlynk?.language) {
      i18n.changeLanguage(cleverlynk.language);
    }
    if (!loadMeta.current && company) {
      if (!document.querySelector("#google-maps") && cleverlynk?.isUsingGeolocation) {
        loadScript(
          "https://maps.googleapis.com/maps/api/js?key=" + process.env.REACT_APP_GOOGLE_API_KEY + "&libraries=places",
          document.querySelector("head"),
          "google-maps"
        );
      }
      // marketingTags.init(company.pixelId, company.crossDomainGA, company.googleAnalyticsId);
      loadMeta.current = true;
      favicon.setFavicon(company?.logoUrl ?? logo, cleverlynk?.name ?? "");
    }
  }, [cleverlynk]);

  useEffect(() => {
    if (!loadedBillingData.current && company?.id && SIC_LIST.includes(company.id)) {
      loadedBillingData.current = true;
      dispatch(fetchCompanyBillingData(company.id));
    }
  }, [company]);

  useEffect(() => {
    initDeliveryAddressCookies();
    initIdentificationCookies();
    handleInitAWSClient();
    if (!document.querySelector("#google-maps")) {
      loadScript(
        "https://maps.googleapis.com/maps/api/js?key=" + process.env.REACT_APP_GOOGLE_API_KEY + "&libraries=places",
        document.querySelector("head"),
        "google-maps"
      );
    }
  }, []);

  const { handleSetGoogleLoaded } = useMapService();
  const googleScript = document.getElementById("google-maps");
  googleScript?.addEventListener("load", () => {
    handleSetGoogleLoaded();
  });

  useEffect(() => {
    if (loadInitialDataSupervisor.status === "succeeded") {
      favicon.setFavicon(company?.logoUrl ?? logo, cleverlynk?.name ?? "");
      handleLocateCityFromClynk();
      if (switchBetweenClynksRef.current) {
        resetCriteria();
      }
      const t = setTimeout(() => {
        enable();
      }, 50);

      return () => clearTimeout(t);
    }
  }, [loadInitialDataSupervisor]);

  useEffect(() => {
    if (chosenClynkWithinGroup && chosenClynkWithinGroup.id !== cleverlynk?.id) {
      history.push({
        pathname: `/${chosenClynkWithinGroup?.id}/redirect${
          location.pathname.includes("checkout") ? "/checkout" : navigateToForm ? "/info" : ""
        }`,
        search: location.pathname.includes("checkout") ? location.search : "",
      });
    }
  }, [chosenClynkWithinGroup]);

  return (
    <ThemeProvider
      theme={createThemeColor(primaryColor ?? "black[500]", {
        text: titleStyles?.textFontFamily ?? "Inter",
        title: titleStyles?.fontFamily ?? "Inter",
      })}>
      <MarketingContextProvider
        pixelId={credentials.FB?.pixelId}
        googleAnalyticsId={credentials.GA?.googleAnalyticsId}
        newGoogleAnalyticsId={credentials.GA?.googleAnalyticsId}
        googleTagManager={company?.googleTagManagerId}
        loadedMarketingScripts={loadedMarketingScripts}>
        <Switch>
          {/* Landing */}
          <Route path="/" exact component={Landing} />
          <Route path="/menu" exact component={Template} />
          <Route path="/info" exact component={UserForm} />
          <Route path="/purchase" exact component={Checkout} />
          <Route path="/unavailable" exact component={UnavailableDialog} />
          <Route path="/coverage" exact component={Coverage} />

          <Route path="/closed" exact component={Landing} />
          <Route path="/:id/info" exact component={UserForm} />
          <Route path="/:id/redirect/info" exact component={UserForm} />
          <Route path="/:companyName/:id/info" exact component={UserForm} />
          <Route path="/:companyName/:id/redirect/info" exact component={UserForm} />

          <Route path="/:id/menu" exact component={Template} />
          <Route path="/:id/redirect/menu" exact component={Template} />
          <Route path="/:companyName/:id/menu" exact component={Template} />
          <Route path="/:companyName/:id/redirect/menu" exact component={Template} />

          <Route path="/:id/purchase" exact component={Checkout} />
          <Route path="/:id/redirect/purchase" exact component={Checkout} />
          <Route path="/:companyName/:id/purchase" exact component={Checkout} />
          <Route path="/:companyName/:id/redirect/purchase" exact component={Checkout} />

          <Route path="/order/summary/:orderId" exact component={OrderSummary} />

          <Route path="/:id/checkout" exact component={Template} />
          <Route path="/:companyName/:id/checkout" exact component={Template} />

          <Route path="/:id/unavailable" exact component={UnavailableDialog} />
          <Route path="/:id/redirect/unavailable" exact component={UnavailableDialog} />
          <Route path="/:companyName/:id/unavailable" exact component={UnavailableDialog} />
          <Route path="/:companyName/:id/redirect/unavailable" exact component={UnavailableDialog} />

          <Route path="/menu/closed" exact component={Template} />
          <Route path="/:id/menu/closed" exact component={Template} />
          <Route path="/:id/redirect/menu/closed" exact component={Template} />
          <Route path="/:companyName/:id/menu/closed" exact component={Template} />
          <Route path="/:companyName/:id/redirect/menu/closed" exact component={Template} />

          <Route path="/:id/coverage" exact component={Coverage} />
          <Route path="/:id/redirect/coverage" exact component={Coverage} />
          <Route path="/:companyName/:id/coverage" exact component={Coverage} />
          <Route path="/:companyName/:id/redirect/coverage" exact component={Coverage} />

          <Route path="/:id" exact component={Landing} />
          <Route path="/:id/redirect" exact component={Landing} />
          <Route path="/:companyName/:id" exact component={Landing} />
          <Route path="/:companyName/:id/redirect" exact component={Landing} />

          <Route path="/:id/closed" exact component={Landing} />
          <Route path="/:id/redirect/closed" exact component={Landing} />
          <Route path="/:companyName/:id/closed" exact component={Landing} />
          <Route path="/:companyName/:id/redirect/closed" exact component={Landing} />
        </Switch>
        <UnavailableDialog />
      </MarketingContextProvider>
      <OpaqueLoading />
      <OpaqueItemLoading />
      <ClynkSnackbar />
    </ThemeProvider>
  );
};

export default App;
